import React from 'react'

import { CalendarIcon, ChevronLeftIcon, ChevronRightIcon } from 'lucide-react'
import { inject, observer } from 'mobx-react'
import { DayPicker } from 'react-day-picker'

import { ExternalCalendarDateRange } from '~/client/src/desktop/enums/ExternalCalendarDateRange'
import { ExternalCalendarDateRangeCommon } from '~/client/src/desktop/enums/ExternalCalendarDateRangeCommon'
import MenuCloser from '~/client/src/shared/components/MenuCloser'
import { Button } from '~/client/src/shared/components/shadcn-ui/button'
import StruxhubSelect from '~/client/src/shared/components/StruxhubInputs/StruxhubSelect'
import Localization from '~/client/src/shared/localization/LocalizationManager'
import ProjectDateStore from '~/client/src/shared/stores/ui/ProjectDate.store'

import CalendarLinkInputStore, {
  DateEndOption,
} from '../CalendarLinkInput.store'

interface IProps {
  store: CalendarLinkInputStore
  openFrom: ExternalCalendarDateRange
  openTo: ExternalCalendarDateRange
  from: string
  to: string
  endOption: DateEndOption
  handler: (key: ExternalCalendarDateRangeCommon, date: string) => void
  projectDateStore?: ProjectDateStore
}

const START_YEAR = 1990

@inject('projectDateStore')
@observer
export default class CalendarDatePicker extends React.Component<IProps> {
  private get store(): CalendarLinkInputStore {
    return this.props.store
  }

  public render(): JSX.Element {
    const { openFrom, openTo, from, to } = this.props
    const isDisabled = this.props.endOption !== DateEndOption.Custom

    return (
      <div className="row px50">
        {this.renderDatePicker(
          openFrom,
          ExternalCalendarDateRangeCommon.FROM,
          from,
        )}
        {this.renderDatePicker(
          openTo,
          ExternalCalendarDateRangeCommon.TO,
          to,
          isDisabled,
        )}
      </div>
    )
  }

  private renderDatePicker(
    openTo: ExternalCalendarDateRange,
    label: ExternalCalendarDateRangeCommon,
    date: string,
    isDisabled: boolean = false,
  ): JSX.Element {
    const { getMonthDayAndYearToDisplay } = this.props.projectDateStore
    const formattedDate = new Date(date)

    return (
      <div className="px5 mt5 no-grow">
        <div className="text bold primary">
          {Localization.translator[label.toLowerCase()]}
        </div>
        <Button
          variant="outline"
          onClick={this.store.setIsOpen.bind(null, openTo)}
          disabled={isDisabled}
        >
          {date
            ? getMonthDayAndYearToDisplay(formattedDate)
            : Localization.translator.chooseDate}
          <CalendarIcon data-icon="inline-end" />
        </Button>
        {this.store.openTo === openTo &&
          this.renderDatePickerDialog(date, label)}
      </div>
    )
  }

  private renderDatePickerDialog(
    date: string,
    label: ExternalCalendarDateRangeCommon,
  ): JSX.Element {
    const { projectDateStore } = this.props
    const { viewingDate, setIsOpen } = this.store
    const currentDate = new Date(viewingDate || date || new Date())
    const selectedDate = date ? new Date(date) : undefined

    return (
      <MenuCloser
        isOpen={true}
        closeMenu={setIsOpen.bind(null, ExternalCalendarDateRange.NONE)}
        className="relative"
      >
        <div className="col bg-white no-grow brada10 ical-calendar-dialog ical-dialog-content bg-white absolute ba-palette-brand-lightest pa5">
          <div className="row">
            <Button
              variant="ghost"
              size="icon-sm"
              onClick={() =>
                this.onMonthChange(currentDate, currentDate.getMonth() - 1)
              }
              className="mx10"
            >
              <ChevronLeftIcon size={16} />
            </Button>
            <div className="text row x-center">
              {projectDateStore.getMonthToDisplay(currentDate)}
            </div>
            <Button
              variant="ghost"
              size="icon-sm"
              onClick={() =>
                this.onMonthChange(currentDate, currentDate.getMonth() + 1)
              }
              className="mx10"
            >
              <ChevronRightIcon size={16} />
            </Button>
            <StruxhubSelect
              isMinimalisticMode
              value={projectDateStore.getYearToDisplay(currentDate)}
              onChange={event =>
                this.onYearChange(currentDate, parseInt(event.target.value))
              }
              className="mw80 ical-year-select"
            >
              {Array.from(
                { length: new Date().getFullYear() - START_YEAR + 1 },
                (_, i) => {
                  const year = START_YEAR + i
                  return (
                    <option key={year} value={year.toString()}>
                      {year}
                    </option>
                  )
                },
              )}
            </StruxhubSelect>
          </div>
          <DayPicker
            mode="single"
            selected={selectedDate}
            onSelect={date => this.selectDate(date, label)}
            month={currentDate}
            hideNavigation
            modifiersClassNames={{
              selected: 'my-selected',
              today: 'my-today',
            }}
          />
        </div>
      </MenuCloser>
    )
  }

  private onMonthChange(date: Date, month: number): void {
    const { viewingDate, setViewingDate } = this.store
    const newDate = new Date(viewingDate || date)
    newDate.setMonth(month)
    setViewingDate(newDate)
  }

  private onYearChange(date: Date, year: number): void {
    const { viewingDate, setViewingDate } = this.store
    const newDate = new Date(viewingDate || date)
    newDate.setFullYear(year)
    setViewingDate(newDate)
  }

  private selectDate(date: Date, label: ExternalCalendarDateRangeCommon): void {
    if (!date) {
      return
    }

    const {
      handler,
      projectDateStore: { convertToProjectDate },
    } = this.props
    handler(label, convertToProjectDate(date).toISOString())
    this.store.setIsOpen(ExternalCalendarDateRange.NONE)
  }
}
